import { UsersService } from './../../services/users.service';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService } from './../../../auth/services/auth.service';
import { UserApp } from './../../models/user-app';
import { Component, Inject } from '@angular/core';
import { ErrorStateMatcher } from '@angular/material/core';
import { FormControl, FormGroupDirective, NgForm } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { ConfirmActionDialogComponent } from 'src/app/shared/components/confirm-action-dialog/confirm-action-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { countries, getCountryData, getEmojiFlag, TCountryCode } from 'countries-list';
import { countryPrefixesAndMasksByIso, removeAllNonDigitsSpacesOfPhone } from 'src/app/shared/utils/phone-mask.util';
import { AsYouType, CountryCode, parsePhoneNumberWithError } from 'libphonenumber-js';
import { ReceiversService } from 'src/app/shared/services/receivers.service';

export interface DialogData {
  title: string;
  operation: string;
  submitText: string;
  promptText: string;
  id: string;
}

export interface ICountry {
  code: string;
  phone: string;
  flag: string;
  name: string;
  continent: string;
}

@Component({
  selector: 'app-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss'],
})
export class UserFormComponent {
  isLoading = false;
  user: UserApp = new UserApp();
  originalUser: UserApp;
  phoneErrorMatcher = new PhoneErrorMatcher();
  emailErrorMatcher = new EmailErrorMatcher();
  crossFieldErrorMatcher = new CrossFieldErrorMatcher();
  countrySelectorErrorMatcher = new CountrySelectorErrorMatcher();
  secureAccessTimeErrorMatcher = new SecureAccessTimeErrorMatcher();
  secureAccessWarningErrorMatcher = new SecureAccessWarningErrorMatcher();
  minSAT: number;
  maxSAT: number;
  defaultSAT: number;
  regeneratedPass = false;
  resetDisarmPass = false;
  whatsappNumber: string;
  generatingPassword = false;
  countriesFlagsPhones: ICountry[] = [];
  selectedCountry: ICountry;
  mask: string | null = '0000000000000';
  phoneNumber: any;

  constructor(
    public authService: AuthService,
    private snackBar: MatSnackBar,
    private dialogRef: MatDialogRef<UserFormComponent>,
    private loadingBar: LoadingBarService,
    private translateService: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    public dialog: MatDialog,
    private abm: UsersService,
    private receiverService: ReceiversService
  ) {
    this.mapCountries();
    this.minSAT = this.authService.getPrimaryCompany().minSecureAccessTime
      ? this.authService.getPrimaryCompany().minSecureAccessTime / 1000
      : 30;
    this.maxSAT = this.authService.getPrimaryCompany().maxSecureAccessTime
      ? this.authService.getPrimaryCompany().maxSecureAccessTime / 1000
      : 300;
    this.defaultSAT = this.authService.getPrimaryCompany().defaultSecureAccessTime
      ? this.authService.getPrimaryCompany().defaultSecureAccessTime / 1000
      : 60;
    dialogRef.disableClose = true;

    dialogRef.backdropClick().subscribe(() => this.onCancelClick());

    if (this.data.operation === 'update') {
      this.loadingBar.useRef().start();
      this.isLoading = true;

      this.abm.getUser(this.data.id).subscribe({
        next: async (res: any) => {
          if (res.success) {
            this.user = { ...res.user };
            this.originalUser = { ...res.user };
            delete this.user.pass;
            delete this.originalUser.pass;
            this.user.secureAccessTime = this.user.secureAccessTime / 1000;
            this.user.secureAccessWarning = this.user.secureAccessWarning
              ? this.user.secureAccessWarning / 1000
              : undefined;
            this.loadingBar.useRef().complete();
            this.isLoading = false;
            this.discoverCountryAndSetNumber();
          } else {
            this.dialogRef.close({
              status: 'error',
              message: await this.translateService.get('User.ErrorGetUser').toPromise(),
            });
            this.loadingBar.useRef().stop();
            this.isLoading = false;
          }
        },
        error: async (error) => {
          this.loadingBar.useRef().stop();
          this.isLoading = false;
          if (error && error.status === 401) {
            this.dialogRef.close({
              status: 'error',
              message: await this.translateService.get('Shared.SessionExpired').toPromise(),
            });
            this.authService.logout();
          } else {
            this.dialogRef.close({
              status: 'error',
              message: await this.translateService.get('User.ErrorGetUser').toPromise(),
            });
          }
        },
      });
    } else if (this.data.operation === 'add') {
      if (this.authService.getCompanies()?.length > 1) {
        this.user.selectedLang = this.authService.getCompanies()[0].language ?? this.authService.defaultLanguage;
      } else {
        this.user.company = this.authService.getPrimaryCompany()._id;
        this.user.selectedLang = this.authService.getPrimaryCompany()?.language
          ? this.authService.getPrimaryCompany()?.language
          : this.authService.defaultLanguage;
      }
      this.user.secureAccessTime = this.defaultSAT;
      this.user.type = 'final';
    }
  }

  get filteredCompanies() {
    return this.authService.getCompanies();
  }

  discoverCountryAndSetNumber = () => {
    if (this.user.whatsappNumber) {
      try {
        const phoneNumber = parsePhoneNumberWithError(this.user.whatsappNumber);
        this.selectedCountry = this.countriesFlagsPhones.find((c) => c.phone === `+${phoneNumber.countryCallingCode}`);
        this.whatsappNumber = this.user.whatsappNumber.replace(this.selectedCountry.phone, '');
        this.onChangeWhatsappNumber();
      } catch (error) {
        this.whatsappNumber = this.user.whatsappNumber;
      }
    }
  };

  isPhoneInvalidCheck = () => {
    const { isPhoneValid } = this.getFormatted();

    return isPhoneValid != undefined && isPhoneValid === false;
  };

  mapCountries = () => {
    const tmpCountriesPhonesByCodes = [];

    Object.keys(countries).forEach((k) => {
      if (['SA', 'NA'].includes(countries[k].continent)) {
        if (countryPrefixesAndMasksByIso[k]) {
          tmpCountriesPhonesByCodes.push({
            code: k,
            phone: `+${countries[k].phone[0]}`,
            name: countries[k].name,
            continent: countries[k].continent,
          });
        }
      }
    });

    this.countriesFlagsPhones = tmpCountriesPhonesByCodes.map((p) => ({
      code: p.code,
      phone: p.phone,
      flag: getEmojiFlag(p.code as TCountryCode),
      name: p.name,
      continent: p.continent,
    }));

    this.countriesFlagsPhones.push(
      {
        name: getCountryData('ES').name,
        code: 'ES',
        flag: getEmojiFlag('ES'),
        continent: 'EU',
        phone: `+${getCountryData('ES').phone[0]}`,
      },
      {
        name: getCountryData('CN').name,
        code: 'CN',
        flag: getEmojiFlag('CN'),
        continent: 'AS',
        phone: `+${getCountryData('CN').phone[0]}`,
      }
    );

    this.countriesFlagsPhones.sort((a, b) => a.name.localeCompare(b.name));

    this.countriesFlagsPhones.unshift({
      name: this.translateService.instant('Shared.Undefined'),
      code: 'globe',
      flag: null,
      continent: null,
      phone: null,
    });
  };

  onChangeCountry = (country: ICountry) => {
    if (country.code !== this.selectedCountry?.code) return;

    this.selectedCountry = country;
    this.whatsappNumber = undefined;
    this.user.whatsappNumber = '';
    this.phoneErrorMatcher.setExists(false);
    this.countrySelectorErrorMatcher.setExists(false);
  };

  getFormatted = () => {
    const formatter: any = new AsYouType(this.selectedCountry?.code as CountryCode);
    let formatted = formatter.input(`${this.selectedCountry?.phone} ${this.whatsappNumber}`);
    formatted = formatter.getNumberValue() ? formatter.formattedOutput : this.whatsappNumber;
    const isPhoneValid = formatter.isValid();
    return { formattedNumber: formatted, isPhoneValid };
  };

  isRequiredCountry = () => {
    const { isPhoneValid } = this.getFormatted();

    return (
      this.selectedCountry == null ||
      this.selectedCountry?.code === 'globe' ||
      (!isPhoneValid && this.whatsappNumber !== undefined)
    );
  };

  onPhoneChange = (event: any) => {
    const el: HTMLInputElement = document.getElementById('whatsappNumber') as HTMLInputElement;

    if (event?.data && !/\d/.test(event?.data)) {
      this.whatsappNumber = this.whatsappNumber.replace(event?.data, '');

      el.value = this.whatsappNumber;
      return;
    }

    this.onChangeWhatsappNumber();
  };

  onChangeWhatsappNumber = () => {
    let { formattedNumber: formatted, isPhoneValid } = this.getFormatted();

    if (formatted?.startsWith(this.selectedCountry?.phone)) {
      formatted = formatted.replace(this.selectedCountry?.phone, '').trim();
    }

    if (this.selectedCountry?.code === 'DO') {
      formatted = formatted.replace('+1 809', '').trim();
    }

    this.whatsappNumber = formatted;

    if (isPhoneValid) {
      this.user.whatsappNumber = `${this.selectedCountry?.phone} ${this.whatsappNumber}`;
      this.phoneErrorMatcher.setExists(false);
      this.countrySelectorErrorMatcher.setExists(false);
    }

    if (this.whatsappNumber === '') {
      this.phoneErrorMatcher.setExists(false);
      this.countrySelectorErrorMatcher.setExists(false);
      this.user.whatsappNumber = '';
    }
  };

  validatePhone = () => {
    if (!this.whatsappNumber) {
      this.phoneErrorMatcher.setExists(false);
      this.countrySelectorErrorMatcher.setExists(false);
    } else {
      if (this.isRequiredCountry()) {
        this.phoneErrorMatcher.setExists(true);
        return false;
      }
      if (this.isPhoneInvalidCheck()) {
        this.phoneErrorMatcher.setExists(true);
        return false;
      }
      if (this.isRequiredCountry()) {
        this.countrySelectorErrorMatcher.setExists(true);
        return false;
      }
    }
    return true;
  };

  submit = () => {
    this.isLoading = true;
    this.loadingBar.useRef().start();

    if (this.data.operation === 'add') {
      this.addUser();
    } else if (this.data.operation === 'update') {
      this.updateUser();
    }
  };

  addUser = async () => {
    if (!this.validatePhone()) return;
    if (this.user.whatsappNumber) this.user.whatsappNumber = removeAllNonDigitsSpacesOfPhone(this.user.whatsappNumber);

    const newUser = {
      ...this.user,
      secureAccessTime: this.user.secureAccessTime * 1000,
      secureAccessWarning: this.user.secureAccessWarning ? this.user.secureAccessWarning * 1000 : undefined,
    };

    if (this.authService.getCompanies().length === 1) {
      newUser.company = this.authService.getPrimaryCompany()._id;
    }

    this.emailErrorMatcher.setUserExists(false);

    this.abm.addUser(newUser).subscribe({
      next: async (res: any) => {
        if (res && res.success) {
          const message = res.reason || (await this.translateService.get('User.UserAdded').toPromise());
          if (res.emailError) {
            navigator.clipboard.writeText(this.user.pass);
          }
          this.dialogRef.close({ status: 'success', message });
          this.loadingBar.useRef().complete();
        } else {
          if (res.code && res.code === 'emailExists') {
            this.emailErrorMatcher.setUserExists(true);
          } else if (res.code && res.code === 'invalidParams') {
            this.dialogRef.close({
              status: 'error',
              message: await this.translateService.get('User.InvalidParam').toPromise(),
            });
          } else if (res.code && res.code === 'EmailUsedOtherCompany') {
            this.dialogRef.close({ status: 'error', message: res.reason });
          } else {
            this.dialogRef.close({
              status: 'error',
              message: await this.translateService.get('User.ErrorAddUser').toPromise(),
            });
          }

          this.loadingBar.useRef().stop();
        }
      },
      error: async (error) => {
        this.isLoading = false;
        this.loadingBar.useRef().stop();

        if (error && error.status === 401) {
          this.dialogRef.close({
            status: 'error',
            message: await this.translateService.get('Shared.SessionExpired').toPromise(),
          });
          this.authService.logout();
        } else {
          this.dialogRef.close({
            status: 'error',
            message: await this.translateService.get('User.ErrorAddUser').toPromise(),
          });
        }
      },
    });
  };

  updateUser = async () => {
    this.emailErrorMatcher.setUserExists(false);
    if (!this.validatePhone()) return;
    const secureAccessWarning = this.user.secureAccessWarning ? this.user.secureAccessWarning * 1000 : undefined;

    // Se borran los datos que no se modificaron.
    const updatedUser = new UserApp();
    updatedUser._id = this.user._id;
    updatedUser.company = this.user.company;
    updatedUser.email = this.originalUser.email !== this.user.email ? this.user.email : undefined;
    updatedUser.name = this.originalUser.name !== this.user.name ? this.user.name : undefined;
    updatedUser.lastName = this.originalUser.lastName !== this.user.lastName ? this.user.lastName : undefined;
    updatedUser.pass = this.regeneratedPass ? this.user.pass : undefined;
    updatedUser.type = this.originalUser.type !== this.user.type ? this.user.type : undefined;
    updatedUser.selectedLang =
      this.originalUser.selectedLang !== this.user.selectedLang ? this.user.selectedLang : undefined;
    updatedUser.secureAccessTime =
      this.originalUser.secureAccessTime !== this.user.secureAccessTime * 1000
        ? this.user.secureAccessTime * 1000
        : undefined;
    updatedUser.secureAccessWarning =
      this.originalUser.secureAccessWarning !== secureAccessWarning ? secureAccessWarning : undefined;
    updatedUser.removeAccessWarning = !secureAccessWarning;
    updatedUser.forceChangeDisarmPass = this.resetDisarmPass ? true : this.originalUser.forceChangeDisarmPass;
    updatedUser.whatsappNumber = removeAllNonDigitsSpacesOfPhone(this.user.whatsappNumber);

    if (this.user.safeWord && this.user.safeWord !== '••••••••' && this.user.safeWord !== this.originalUser.safeWord) {
      updatedUser.safeWord = this.user.safeWord;
    } else if (this.user.safeWord === '') {
      updatedUser.safeWord = '';
    }

    this.abm.modifyUser(updatedUser).subscribe({
      next: async (res: any) => {
        if (res && res.success) {
          const message = res.reason || (await this.translateService.get('User.UserMod').toPromise());
          if (res.emailError) {
            navigator.clipboard.writeText(this.user.pass);
          }
          this.dialogRef.close({ status: 'success', message });
          this.loadingBar.useRef().complete();
        } else {
          if (res.code && res.code === 'emailExists') {
            this.emailErrorMatcher.setUserExists(true);
          } else if (res.code && res.code === 'invalidParams') {
            this.dialogRef.close({
              status: 'error',
              message: await this.translateService.get('User.InvalidParam').toPromise(),
            });
          } else if (res.reason) {
            this.dialogRef.close({
              status: 'error',
              message: res.reason,
            });
          } else {
            this.dialogRef.close({
              status: 'error',
              message: await this.translateService.get('User.ErrorModUser').toPromise(),
            });
          }

          this.loadingBar.useRef().stop();
        }
      },
      error: async (error) => {
        this.isLoading = false;
        this.loadingBar.useRef().stop();

        if (error && error.status === 401) {
          this.dialogRef.close({
            status: 'error',
            message: await this.translateService.get('Shared.SessionExpired').toPromise(),
          });
          this.authService.logout();
        } else {
          this.dialogRef.close({
            status: 'error',
            message: await this.translateService.get('User.ErrorModUser').toPromise(),
          });
        }
      },
    });
  };

  resetingDisarmPass = async () => {
    let msgDialog;
    let btMsgDialog;
    if (this.resetDisarmPass) {
      msgDialog = await this.translateService.get('User.ResetPassCancel').toPromise();
      btMsgDialog = await this.translateService.get('User.CancelReset').toPromise();
    } else {
      msgDialog = await this.translateService.get('User.DeletePass').toPromise();
      btMsgDialog = await this.translateService.get('User.Reset').toPromise();
    }
    this.openConfirmDialog(msgDialog, btMsgDialog, (result: boolean) => {
      if (result) {
        this.resetDisarmPass = !this.resetDisarmPass;
      }
    });
  };

  generatePassword = async () => {
    this.regeneratedPass = true;
    const generate = () => {
      this.generatingPassword = true;
      this.user.pass = '';

      this.abm.generatePass().subscribe({
        next: async (res: any) => {
          if (res && res.success) {
            this.user.pass = res.newPassword;
          } else {
            this.snackBar.open(
              await this.translateService.get('User.ErrorGeneratePass').toPromise(),
              await this.translateService.get('Shared.Close').toPromise(),
              { duration: 10000 }
            );
          }

          this.generatingPassword = false;
        },
        error: async (error) => {
          this.generatingPassword = false;

          if (error && error.status === 401) {
            this.dialogRef.close({
              status: 'error',
              message: await this.translateService.get('Shared.SessionExpired').toPromise(),
            });
            this.authService.logout();
          } else {
            this.snackBar.open(
              await this.translateService.get('User.ErrorGeneratePass').toPromise(),
              await this.translateService.get('Shared.Close').toPromise(),
              { duration: 10000 }
            );
          }
        },
      });
    };

    if (this.data.operation === 'update') {
      this.openConfirmDialog(
        await this.translateService.get('User.GenerateNewPass').toPromise(),
        await this.translateService.get('User.Generate').toPromise(),
        (result: boolean) => {
          if (result) {
            generate();
          }
        }
      );
    } else {
      generate();
    }
  };

  openConfirmDialog(promptText: string, submitText: string, callback: Function): void {
    const dialogRef = this.dialog.open(ConfirmActionDialogComponent, {
      maxWidth: '450px',
      autoFocus: false,
      data: { promptText, submitText },
    });

    dialogRef.afterClosed().subscribe((result) => callback(result));
  }

  onCancelClick = async () => {
    this.openConfirmDialog(
      await this.translateService.get('User.ExitForm').toPromise(),
      await this.translateService.get('Shared.Leave').toPromise(),
      (result: boolean) => {
        if (result) {
          this.dialogRef.close({ status: 'cancel' });
        }
      }
    );
  };

  validateSecureAccessTime = () => {
    this.secureAccessTimeErrorMatcher.setExists(false);
    if (this.user.secureAccessTime < this.minSAT || this.user.secureAccessTime > this.maxSAT) {
      this.secureAccessTimeErrorMatcher.setExists(true);
    }
  };

  validateSecureAccessWarning = () => {
    this.secureAccessWarningErrorMatcher.setExists(false);
    if (
      this.user.secureAccessWarning &&
      (this.user.secureAccessWarning < this.minSAT || this.user.secureAccessWarning > this.maxSAT)
    ) {
      this.secureAccessWarningErrorMatcher.setExists(true);
    }
  };

  isValidData(): boolean {
    return (
      !this.secureAccessTimeErrorMatcher.getExists() &&
      !this.secureAccessWarningErrorMatcher.getExists() &&
      !this.isLoading &&
      !this.emailErrorMatcher.getUserExists() &&
      this.user.email !== undefined &&
      this.user.email !== '' &&
      this.user.name !== undefined &&
      this.user.name !== '' &&
      this.user.lastName !== undefined &&
      this.user.lastName !== '' &&
      this.validatePhone()
    );
  }
}

export class CrossFieldErrorMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    return (control.dirty && control.errors) || (form.invalid && form.errors && form.errors.validTimerRange);
  }
}

export class PhoneErrorMatcher implements ErrorStateMatcher {
  PhoneExists = false;

  public getExists(): boolean {
    return this.PhoneExists;
  }

  public setExists(val: boolean) {
    this.PhoneExists = val;
  }

  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    return (
      this.PhoneExists ||
      ((control.touched || form.submitted) && control.errors !== undefined && control.errors !== null)
    );
  }
}

export class CountrySelectorErrorMatcher implements ErrorStateMatcher {
  CountrySelectorExists = false;

  public getExists(): boolean {
    return this.CountrySelectorExists;
  }

  public setExists(val: boolean) {
    this.CountrySelectorExists = val;
  }

  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    return (
      this.CountrySelectorExists ||
      ((control.touched || form.submitted) && control.errors !== undefined && control.errors !== null)
    );
  }
}

export class SecureAccessTimeErrorMatcher implements ErrorStateMatcher {
  SecureAccessTimeExists = false;

  public getExists(): boolean {
    return this.SecureAccessTimeExists;
  }

  public setExists(val: boolean) {
    this.SecureAccessTimeExists = val;
  }

  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    return (
      this.SecureAccessTimeExists ||
      ((control.touched || form.submitted) && control.errors !== undefined && control.errors !== null)
    );
  }
}

export class SecureAccessWarningErrorMatcher implements ErrorStateMatcher {
  SecureAccessWarningExists = false;

  public getExists(): boolean {
    return this.SecureAccessWarningExists;
  }

  public setExists(val: boolean) {
    this.SecureAccessWarningExists = val;
  }

  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    return (
      this.SecureAccessWarningExists ||
      ((control.touched || form.submitted) && control.errors !== undefined && control.errors !== null)
    );
  }
}

export class EmailErrorMatcher implements ErrorStateMatcher {
  EmailExists = false;

  public getUserExists(): boolean {
    return this.EmailExists;
  }

  public setUserExists(val: boolean) {
    this.EmailExists = val;
  }

  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    return (
      this.EmailExists ||
      ((control.touched || form.submitted) && control.errors !== undefined && control.errors !== null)
    );
  }
}
